import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { z } from 'zod';
import { toast } from 'sonner';
import { FirebaseError } from 'firebase/app';
import { UploadTask } from 'firebase/storage';
import React, { useState, useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm, useFieldArray, SubmitErrorHandler } from 'react-hook-form';

import { UploadBox } from './UploadBox.tsx';
import { UploadedFilesContent } from './UploadContent.tsx';
import { handleFilesUpload, handleRemoveFile } from './utils.ts';
import { DEFAULT_ACCEPTED_FILE_TYPES, DEFAULT_MAX_FILES_UPLOAD, MAX_INDIVIDUAL_DOCUMENT_SIZE_MB } from './constants.ts';

const validationSchema = ({ maxFiles }: { maxFiles: number }) =>
  z
    .object({
      files: z
        .array(
          z.object({
            file: z
              .instanceof(File)
              .refine(
                (file) => file.size <= MAX_INDIVIDUAL_DOCUMENT_SIZE_MB * 1024 * 1024,
                `File size should not exceed ${MAX_INDIVIDUAL_DOCUMENT_SIZE_MB}MB`,
              ),
          }),
        )
        .min(1, 'At least one document is required')
        .max(maxFiles, `A maximum of ${maxFiles} file${maxFiles > 1 ? 's' : ''} can be uploaded at once`),
      description: z.string(),
    })
    .superRefine((val, ctx) => {
      if (val.description.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Description is required',
          path: ['description'],
        });
      }
    });

const getToast = (noToast: boolean) => {
  if (noToast) {
    return undefined;
  }

  return toast;
};

const handleSuccesses = ({ successfulUploadsCount, noToast }: { successfulUploadsCount: number; noToast: boolean }) => {
  if (successfulUploadsCount < 1) {
    return;
  }

  const isMultipleUploads = successfulUploadsCount > 1;

  getToast(noToast)?.success(`Successfully uploaded ${successfulUploadsCount} file${isMultipleUploads ? 's' : ''}`, {
    description: `The file${isMultipleUploads ? 's' : ''} will be available once ${isMultipleUploads ? 'they are' : 'it is'} processed.`,
  });
};

const extractErrorCauseData = (error: unknown) => {
  // Check if the error is an object and has a 'cause' property
  // The 'in' operator tests if a property exists on an object or its prototype chain
  // Modern Error objects can have a 'cause' property containing additional error details
  if (!error || typeof error !== 'object' || !('cause' in error)) {
    return null;
  }

  const cause = (error as { cause: unknown }).cause;

  if (!cause || typeof cause !== 'object') {
    return null;
  }

  const causeObj = cause as Record<string, unknown>;

  return {
    failedUploads: Array.isArray(causeObj.failedUploads) ? causeObj.failedUploads : null,
    successfulUploads: Array.isArray(causeObj.successfulUploads) ? causeObj.successfulUploads : null,
  };
};

export type UploadFilesFormData = z.infer<ReturnType<typeof validationSchema>>;
export type UploadFilesOnSubmitReturn = {
  name: string;
  index: number;
  uploadTask?: UploadTask;
} & (
  | {
      id: string;
      status: 'fulfilled';
    }
  | {
      status: 'rejected';
      id: string | null;
      reason?: unknown;
    }
);

const DEFAULT_UPLOAD_OPTIONS = {
  acceptedFileTypes: DEFAULT_ACCEPTED_FILE_TYPES,
} satisfies UploadFileModalProps['uploadOptions'];

export type UploadFileModalProps = {
  isOpen: boolean;
  handleCloseOnClick: () => Promise<void> | void;
  onSubmit: (data: UploadFilesFormData) => Promise<UploadFilesOnSubmitReturn[]>;
  maxFiles?: number;

  /**
   * @description
   * The options for the upload box.
   *
   * @default
   * {
   *   acceptedFileTypes: DEFAULT_ACCEPTED_FILE_TYPES,
   * }
   */
  uploadOptions?: Omit<
    React.ComponentProps<typeof UploadBox>,
    'id' | 'onChange' | 'setError' | 'maxFiles' | 'disabled'
  >;
  noToast?: boolean;
  description?: React.ReactNode;
  defaultValues?: Partial<UploadFilesFormData>;
};

export const UploadFileModal = ({
  handleCloseOnClick,
  isOpen,
  onSubmit,
  description,
  maxFiles = DEFAULT_MAX_FILES_UPLOAD,
  noToast = false,
  uploadOptions = DEFAULT_UPLOAD_OPTIONS,
  defaultValues,
}: Readonly<UploadFileModalProps>) => {
  const isMobile = useMediaQuery('(max-width: 320px)');
  const [uploadTasks, setUploadTasks] = useState<Record<string, UploadTask | undefined>>({});

  const methods = useForm<UploadFilesFormData, UploadFilesFormData, UploadFilesFormData>({
    defaultValues: {
      description: defaultValues?.description ?? '',
      files: defaultValues?.files ?? [],
    },
    resolver: zodResolver(validationSchema({ maxFiles })),
  });

  const {
    control,
    formState: { errors, isSubmitting, isValid, isDirty },
    getValues,
    handleSubmit,
    setError,
    reset,
  } = methods;

  const { fields, append, remove } = useFieldArray<UploadFilesFormData>({
    control,
    name: 'files',
  });

  const handleOnClose = useCallback(() => {
    // Cancel any ongoing uploads
    Object.values(uploadTasks).forEach((task) => {
      task?.cancel();
    });

    handleCloseOnClick();
    reset();
    setUploadTasks({});
  }, [uploadTasks, handleCloseOnClick, reset]);

  // Create a Promise for each upload task
  const createUploadPromise = ({
    uploadTask,
    fileName,
    index,
  }: {
    uploadTask: UploadTask;
    fileName: string;
    index: number;
  }): Promise<{ fileName: string; index: number; result: 'SUCCESS' | 'FAILED' | 'CANCELLED' }> => {
    return new Promise((resolve) => {
      const unsubscribe = uploadTask.on('state_changed', {
        error: (error) => {
          if (error instanceof FirebaseError && error.code === 'storage/canceled') {
            getToast(noToast)?.warning('You cancelled an upload', {
              description: `You cancelled your upload of ${fileName}.`,
            });

            unsubscribe();
            resolve({ index, fileName, result: 'CANCELLED' });

            return;
          }

          unsubscribe();
          resolve({ index, fileName, result: 'FAILED' });
        },
        complete: () => {
          // Upload completed successfully
          unsubscribe();
          resolve({ index, fileName, result: 'SUCCESS' });
        },
      });
    });
  };

  const clearUploadTasks = () => {
    setUploadTasks({});
  };

  const internalHandleFilesUpload = (files: FileList) => {
    handleFilesUpload({
      files,
      append,
      methods,
      errors,
      numberOfUploadFields: 1,
      maxFilesUpload: maxFiles,
      allowZip: uploadOptions?.allowZip,
      acceptedFileTypes: uploadOptions.acceptedFileTypes ?? DEFAULT_UPLOAD_OPTIONS.acceptedFileTypes,
    });
  };

  const onRemoveFile = (index: number) => {
    handleRemoveFile({ index, remove, methods, name: 'files', errors, maxFilesUpload: maxFiles });
  };

  const handleOnSubmit = async () => {
    const formData = getValues();

    const { files, description } = formData;

    if (files.length === 0 || !description) {
      return;
    }

    try {
      const results = await onSubmit(formData);

      const newUploadTasks = results.reduce<Record<string, UploadTask | undefined>>((acc, result) => {
        const fileId = fields[result.index]?.id;

        if (!fileId) {
          return acc;
        }

        return {
          ...acc,
          [fileId]: result.uploadTask,
        };
      }, {});

      setUploadTasks(newUploadTasks);

      // Create promises for all upload tasks
      const uploadPromises = results
        .filter((result) => result.uploadTask)
        .map((result) =>
          createUploadPromise({
            uploadTask: result.uploadTask!,
            fileName: result.name,
            index: result.index,
          }),
        );

      // Wait for all uploads to complete
      const uploadResults = await Promise.allSettled(uploadPromises);

      clearUploadTasks();

      // Count successful and failed uploads
      const successfulUploads = uploadResults.filter(
        (result) => result.status === 'fulfilled' && result.value.result === 'SUCCESS',
      );

      const failedUploads = uploadResults.filter(
        (result) => result.status === 'rejected' || (result.status === 'fulfilled' && result.value.result === 'FAILED'),
      );

      // Show completion messages
      handleSuccesses({ successfulUploadsCount: successfulUploads.length, noToast });

      if (failedUploads.length > 0) {
        throw new Error('Failed to upload', {
          cause: {
            failedUploads,
            successfulUploads,
          },
        });
      }

      handleOnClose();
    } catch (error) {
      const causeData = extractErrorCauseData(error);
      if (causeData?.failedUploads) {
        if (causeData.successfulUploads) {
          remove([...causeData.successfulUploads.map((result) => result?.value?.index)].filter((v) => v !== undefined));
        }

        setError('files', {
          message: `We could not upload ${causeData.failedUploads.length} file${causeData.failedUploads.length > 1 ? 's' : ''}. Please try again later.`,
        });

        return;
      }

      setError('files', {
        message: `We could not upload ${files.length > 1 ? 'some of your' : 'your'} file${files.length > 1 ? 's' : ''}. Please try again later. If the issue persists, please contact support.`,
      });
    }
  };

  const handleOnSubmitError = (errors: Parameters<SubmitErrorHandler<UploadFilesFormData>>[0]) => {
    if (errors.files && Array.isArray(errors.files)) {
      errors.files.forEach((error, index) => {
        setError(`files.${index}`, {
          message: error.file?.message ?? error.message,
        });
      });
    }

    toast.error('There is an issue with your submission', {
      description: 'Please ensure you have filled in all the required fields and try again.',
    });
  };

  const handleOnCloseFromDialog = () => {
    // Don't close the dialog via backdrop click if the form is dirty or the form is submitting
    if (isSubmitting || isDirty) {
      return;
    }

    handleOnClose();
  };

  return (
    <Dialog open={isOpen} fullWidth fullScreen={isMobile} onClose={handleOnCloseFromDialog}>
      <FormProvider {...methods}>
        <DialogTitle>Upload document{maxFiles > 1 ? '(s)' : ''}</DialogTitle>
        <DialogContent>
          {!!description && (
            <Stack gap={1} direction="column" width="100%" pb={2}>
              {description}
            </Stack>
          )}
          <Stack gap={2} direction="column" width="100%">
            <Controller
              control={control}
              name="description"
              disabled={isSubmitting}
              render={({ field: { ref: _ref, ...fieldProps } }) => (
                <TextField
                  {...fieldProps}
                  placeholder={`The description of the document${maxFiles > 1 ? '(s)' : ''}`}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                />
              )}
            />

            <UploadBox
              id={'files'}
              onChange={internalHandleFilesUpload}
              setError={setError}
              maxFiles={maxFiles}
              isUploading={isSubmitting}
              {...uploadOptions}
            />

            {errors.files?.types &&
              Object.entries(errors.files.types).map(([errorType, errorMessage]) => (
                <Typography key={errorType} variant="body2" color="secondary">
                  {errorMessage}
                </Typography>
              ))}

            {errors.files?.message && (
              <Typography variant="body2" color="secondary">
                {errors.files.message}
              </Typography>
            )}

            <UploadedFilesContent
              items={fields.map((field) => ({ ...field, uploadTask: uploadTasks[field.id] }))}
              onRemoveFile={onRemoveFile}
              maxFiles={maxFiles}
              isUploading={isSubmitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOnClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleOnSubmit, handleOnSubmitError)}
            disabled={!isValid}
            loading={isSubmitting}
            variant="contained"
          >
            Submit
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
